import {Injectable} from "@angular/core";
import {ChannelRepository} from "../repository/channel.repository";
import {BehaviorSubject, Subject} from "rxjs";
import {debounceTime, takeUntil} from "rxjs/operators";
import {Channel} from "../models/channel.model";
import {ChannelService} from "../channel.service";
import {Topic} from "../models/topic.model";
import {DEFAULT_AVATAR} from "./smart-link-popup.component.service";
import {ConversationRepository} from "../../talk/repositories/conversation.repository";
import {ContactRepository} from "../../talk/repositories/contact.repository";
import {UntypedFormControl} from "@angular/forms";
import {CommonUtil} from "../../talk/utils/common.util";
import {AvatarRepository} from "../../talk/repositories/avatar.repository";
import {ConfigService} from "../../config.service";
import {Contact} from "../../talk/models/contact.model";
import {Store} from "@ngrx/store";
import {TalkRootState} from "../../talk/reducers";
import {getUserConfig} from "../../reducers";
import {combineLatest} from "rxjs";
import {ChannelRootState, getAllTopicsInfo} from "../store/reducers";


@Injectable()
export class SmartLinkPopupFiltersService {
  constructor(private _channelRepository: ChannelRepository,
              private _conversationRepository: ConversationRepository,
              private _contactRepository: ContactRepository,
              private _channelService: ChannelService,
              private _avatarRepository: AvatarRepository,
              private _configService: ConfigService,
              private _talkRootStore: Store<TalkRootState>,
              private _channelRootStore: Store<ChannelRootState>) {
    // this.fetchAllFilters();
  }


  channelTypeOptions = [
    { label: "All Channels", value: "all" },
    { label: "Public only", value: "public" },
    { label: "Private only", value: "private" },
  ];
  topicTypeOptions = [
    { label: "All Topics", value: "all" },
    { label: "Public only", value: "public" },
    { label: "Private only", value: "private" },
  ];
  $channelOptions = new BehaviorSubject<SearchFilterChipOption[]>([]);
  $topicOptions = new BehaviorSubject<SearchFilterChipOption[]>([]);
  $userOptions = new BehaviorSubject<SearchFilterChipOption[]>([]);
  isAlive$ = new Subject<boolean>();

  userSearchControl = new UntypedFormControl("");

  private _topicsData = { offset: 0, limit: 25 };
  private _totalTopics = 0;
  private _allContacts: any[] = [];


  fetchAllFilters() {
    this.fetchChannels();
    this.fetchTopics();
    this.fetchAllUsers();
  }


  /* CHANNELS FILTERS */
  loadMoreChannels() {
    const offset = this.$channelOptions.getValue().length;
    this._channelRepository.loadMoreChannels(offset);
  }

  private fetchChannels() {
    this._channelRepository.selectAllChannels().pipe(takeUntil(this.isAlive$)).subscribe((channels: Channel[]) => {
      this.$channelOptions.next(channels.map(channel => ({ label: channel.name, value: channel?.uid, imgSrc: channel?.avatar_url || DEFAULT_AVATAR })));
    });
  }


  /* TOPICS FILTERS */
  loadMoreTopics() {
    const loadedTopicOptions = this.$topicOptions.getValue().length;
    if (loadedTopicOptions < this._totalTopics) this.fetchTopics();
  }

  private fetchTopics() {
    this._channelRepository.setAllTopics().pipe(takeUntil(this.isAlive$)).subscribe(topics => this.setAllTopics(topics));
  }

  getAllTopics() {
    this._channelRootStore.select(getAllTopicsInfo).subscribe(info => {
      if (info?.ids?.length === 0 && !info.isLoading && !info.isLoaded) {
        this._channelRepository.loadAllTopics();
      }
    });
  }

  private setAllTopics(topics: Topic[]) {
    const filterAndConcat = (oldTopicOptions: SearchFilterChipOption[], newTopicOptions: SearchFilterChipOption[]) => {
      const resultantTopicOptions = [...oldTopicOptions];
      newTopicOptions.forEach(newTopicOption => {
        const index = oldTopicOptions.findIndex(oldTopic => oldTopic.value === newTopicOption.value);
        if (index === -1) {
          resultantTopicOptions.push(newTopicOption);
        }
      });
      return resultantTopicOptions;
    };
    const newTopicOptions: SearchFilterChipOption[] = topics.map(topic => {
      return {
        label: topic?.subject,
        value: topic?.id,
        imgSrc: topic.attachments.filter(attachment => attachment.is_header)?.[0]?.thumbnail_url || DEFAULT_AVATAR
      };
    });
    this.$topicOptions.next(filterAndConcat(this.$topicOptions.getValue(),newTopicOptions));
  }

  private buildAvatarUrl(jid) {
    const avatarVersion = "";
    let avatarName = this._avatarRepository.buildTargetHash(jid);
    const avUrl = this._configService.avatarServiceUrl + "/" + avatarName + ".jpg" + avatarVersion;
    return CommonUtil.translateHINFileURL(avUrl);
  }

  /* USERS FILTERS */
  private fetchAllUsers() {
    const getMappedUsers = (contacts: any[]): any[] =>
      contacts.map(contact => ({
        label: contact.name || contact.fullName,
        value: contact.id,
        imgSrc: this.buildAvatarUrl(contact.bare) || DEFAULT_USER_AVATAR
      }));
    const getSortedUsers = (contacts: any[]): any[] => {
      return contacts.sort((a, b) => {
        if (a.label.toUpperCase() < b.label.toUpperCase()) { return -1; }
        if (a.label.toUpperCase() > b.label.toUpperCase()) { return 1; }
        return 0;
      });
    };
    const $loggedInUserOb = this._talkRootStore.select(getUserConfig);
    const $contactsOb = this._contactRepository.getAllContacts().pipe(takeUntil(this.isAlive$));
    combineLatest([$loggedInUserOb, $contactsOb]).subscribe(res => {
      const loggedInUser = {
        fullName: `${res[0].firstname} ${res[0].lastname}`,
        id: res[0].id,
        bare: res[0].mail
      };
      this._allContacts = [...res[1], loggedInUser];
      this.$userOptions.next(getSortedUsers(getMappedUsers([...res[1], loggedInUser])));
    });

    this.userSearchControl.valueChanges.pipe(takeUntil(this.isAlive$), debounceTime(500)).subscribe(async () => {
      const mappedContacts = this.getFilteredRecipients(this.userSearchControl.value);
      this.$userOptions.next(getSortedUsers(getMappedUsers(mappedContacts)));
      combineLatest([$loggedInUserOb, $contactsOb]).subscribe(res => {
        if (!!res) {
          const loggedInUser = {
            fullName: `${res[0].firstname} ${res[0].lastname}`,
            id: res[0].id,
            bare: res[0].mail
          };
          this._allContacts = [...res[1], loggedInUser];
          this.$userOptions.next(getSortedUsers(getMappedUsers([...res[1], loggedInUser])));
        }
      });
    });
  }

  private getFilteredRecipients(searchValue: string): Contact[] {
    return this._allContacts.filter((contact: Contact) =>
      (contact.name || contact.fullName).toLowerCase().includes(searchValue.toLowerCase()) &&
      (contact.bare).toLowerCase().includes(searchValue.toLowerCase())
    );
  }
}

export interface SearchFilterChipOption {
  label: string;
  value: string | number;
  imgSrc: string;
}

export const DEFAULT_USER_AVATAR = "\ndata:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGwAAABsCAYAAACPZlfNAAAAAXNSR0IArs4c6QAADuxJREFUeAHtXUuMHMUZrup57Dz2vV4wxsYkOFopmEccmxiMcrCUSzCOkotzIYo5GCU3ELk4BxSk+BIUbkhsJBMlF5wDEo/kEsmHCIPBGwPGjmQFC7AxGK/Xu96d1+7OTOf7erZne3bn1TNdVT1hSxp1Tdfrr//rev31119S9JCzbVvm8/mtZcuasEtiwpLlCby6x5ZiWNpiQEgxgDgDUsoBVgv+BfgXhC0WEGcBceaktC+VbeuijIiLVrl8MZlMfoE4dq+wQYaZUDA8BoD22FLut22xH7Q+hHfpIGkGWFnk976U4qS07ZMA8AzeLQdZRpB5hQ4wADKayy0esoV9EK3iUTST/iAr3DIvKTNob29LIV9Ppfr+BvButkyjMUIoAGNLyuWWflyWpV+AUQfwP66RBw2LAlhL+HDesuzIX1Kp+D/C0PKMAgZgxrO5wtNgyhFwbawh58IRMIOPaTKdSrwI4KZNkWQEsFzOvrMs8s8KIY8AtJSpyndSLsDKYTozaYnkC6mUvNpJHt2k0QrYgm3fJgqF32GMeDIs3V6nzHO6SymOi0TiOUxLr3eaj990WgADOBYmEk+VRfkYCBz2S2TI489ZwjqKCcrLALGsmlblgGWzS5iWF1/CtHy36sqYzB/LgilpR3+VTsenVNJhqcocrSqWyRX+YMvS6f93sMhD1hF1fc+pM+quiq9KWhgWu3cVbfsEJAx7VREe6nylOB2V8hAW4ZeDpjPwFpbNFg4Wy/aH31iwiBA+VPKAvAgasEBb2EI2/3tQezRoIuvlhy5X5PKLmHQWRGFxWSwuL4niclGUSpjalMuihF80EhHRaETE4zGRTCREOolfOikwOaiXpaJ38thAOvnboDIPhHIwL4oF8CQWwIeDIqxePsViUczNL4j5hZzI5fKiDND8ukjEEoMD/WLT6JADot/0ncTHgvsVLLiP4EMpdpLem6ZrwABWMpvPnwDvHvdmHKQ/k82L6ZlZsZChnDY419+fEnfcNqYFODTqN9PJ5CGAlu+mBl0BBrCGM/nCW5gi7euGiEZpc/mC+PLaNLq+QqMogbzfNDosNgM4ywp8SK+lT8pT/cnEAYA2VxvQ/r+OAWPLAlj/VAXWl19PixszHderfQ6sxOzDOLd92xaR6FMsd66A9qNOW1pHgDljVj7/mqpu8LMrX2KcCrb7awdBtrBtd94uhjDGqXQr3eNPAVrJbzkd9QHOBEPRmHUDY5UJsMg4zi4/v/KVmLu14JePvuLzQwcP/+Qr0Upk34Bx6q5yNsjpuGl3+eo1MTunGDTMqCvLIH+19QVYZSGodp1VWFzyVwNFsb/46prIZLGTotTZR/0urtsewyBu2o7V+wegf0RVHWZmb4mrX2nbqWhZDa7Z7rl7m+qJyGzUkg+2K8Zqq4VhkhGDbPBVVWCVy7ZgNxQmsIgmpSbXrs+0BLbLCCOUu5LH7eTTFmDZ/OIxlbLBzy5fVT7Qt8OMenHmFzIiX1isFxTcO8geHR63kWNLwLifBfngM23k1VGUm7PzIgMxU5jd9WkdilP2MxVeN+dEU8DQTK3K5qPdNF7zIpqHzmcyzSOEIJQiMfBCKSXt8ropENzWB51Kd4pVi52C4DKFzNwZUO3Ia/K8WTkNAaPCzIoORrP0XYXxqy0WfS/2uyqz08Tqp/gVyshzR1mpAaENAcNG0/NIo1RhZgn7V73iCqonHquMGKZm2erfWl9dwKg3CFW0w7VRg/+3jP2tXnHFkj5aqQZIDOrxpi5gVPJEd6VYbI11To90h2RcEWsyXY68ryjari9xHWCIPE6N3PVRg39TLPXG+MWacxGt1zla0cCi1q0DzNF116Q+Tel4rzjdtKLhpIjFWv7UAIZIsZWDCWvjKflPkVSvuIjq3eg6jCAWxMQbVAMYjvw8hsCwnyLx0q/NT0GwATfGY1jecmuowPmsJ7yBqv3YcVVdRGD5R6AyZ8LxzJy33CpgaHqjPEznDVTtp85gr7iIZYbWlQOOoy6fqoA5x1Q1TOXdgvmk4kuvuFg8aoRUNKQ4sXELrwKGAS5wtWK3kEbPvljvANafSjaqhvL3XmwcwJzZIQ+Aa3bRWFS9LmBAdUqbBAzYuLNFBzCaVsD+gVrdrgaM60+H/8RsH3QVjY63wMbBCDystDDYwWjAT+WvhweNfCe+6tWfMv9R0VYJia4AVjFa4qsSQUUeGEhrPk3in/J0OuE/UcApsBPlAAbjL7bM5gvYggnWwkwrer+enhHXb8yKVKIPR4WK2BfTJw1vRdva8AEcmsjlCiKR7BP3bN+6NljLf6xZszguNWChb9yqGyzW8NZ8xtl2x8cSarBI60Im55w3y+IUTS5vRv+EGBEri4a2SJRuhybek27Z4JYQsbJoFc0E5wzJ5rquqgkhsEu0Y8GOJuzcFzqf8R5aNHv5wuO3phyxsjDn2GGCgFjMjGyum7palhQmPzRiBb1DtYo2jRiU6OtrFBTa90kcajfpiJUFhQ8jK9eUQVFPp0w3vYAmVhbNrnZagW7SUVJvVNzTAfFDWOQbdcAKY5htBDBWfNA0A3xwn2efE1jkm3TEysIK2hhgm0aU6qkGytsxWBow7YiVI0s0RQi/2E1jys4HBlatWDQmRoYGA8uvm4zYJao9zNuCOho2ScGsUJjdljs2Yd/OvP4JsWKXaBQwlC/uvmuLMLmj2+xjuX18TLkZiGble8OIVRQnK40CRoI4W/z23VsdIfCNm3OOFN9LqAl/LBoVWzaPi6Ew7dcBKy6cjQPmAhIFk/rThqfO/ICg0jaxY3u4wAJdxIoLZ332gVxkmjxThqUJJK2vLxZKXRNihTHMvtSEf9qDOLinkuY0lFjhsIrNiJXFi2O0o9KiQO7wmnQ0yxdGR6ws3vITNuIGDTKMs9aBkGpyOTcy8UqmsAFGqbiptRlNzCq3m9ghw4mVtXJ/ln5bdy2INiUKGh40JqlryhG0/Cyx4sKZ2hXvN41tIJDrH93SBZYXqnVXLd9xx5msGExBt32yNsz8P2e2qHnPbGR4SJjU2WjGdRcjR/jLm+maRTYVRp1FXY6L9tvHq6d6dBXbdjkuRg5g6BvPQP02dDaEYhoVdSjLpIQjlA7YOBiBuEoLw52PGMneDhuxOhloUhuqFd+JDcavZcar7ofhpN8brRLqDgeR2orUWJTvOgGb191EVcBw/9UJMCgc9ltXqNMHl17DKS7z23kSE16e6satAoaAmzjp95YbEIZnGXs/utziUqi+1Wq1iQmxcV9UAeML3qbqBoThaWu048HTKdjRDUO1a2hYi0kNYLz6FrFnalIY/LOs0dobLd3wdErI3MwKJlWyagBD01vGADdZDTXsWVp2JkbaqJi9Na+trHYKIhbExBu3BjAGrNxTrNpgu5eGhn7lxpHXlMwza2ExuAmgcsRiDYmr03o3ABFxqbRtvJXxRKZu87I0FXvj5qzLCsNPm60LWNS6dS2MwbxUGpGNTptobduEm8aNSqZvpyDviUG9+tcFjDeAQ+HjeL0EOt4tLi1Dc6o6k9VRZLUMzhR56YFuc3tVAuAh7xvdwt5wbeoYCs7lubmpVUeZdoB58YDpr5x6Jd+CvqSBk6JzUGqZaHT7et0WRrSZgDeA06/DcQnEG4U++fSycbBYXx4+/+TTKwJGJnVUv1oGed4ILEZq2MIYiO7Bwv2W74GZSmzX8+opXj6axY9ghdVo89jIkBjHGQDVAmLIM6dwT+YPMIaVyf96rilgTFC5mp63nbe+HYLdGZlewo/TYxo2pl1fjge2zet6IWgB+lwQM15YptD1GFPvHY3AjGGTk1pdYGq9KB2/I0jSjuxNp+NnmmXSVqmV69rLz3ozIvPzsLGBFuhMvyna6SWjy966+PVTSYenMamwQ8XXRCLe9U61lNYL/anEb1rR0hZgaBWxTD7/r6Wl4t5buG6QC8wcLrzecKsciMMyHbtM54eNVxoUS8Tjzv+WrVGK0/3J5A8Rr0aqsZr7qq8twBj9woVLd83ls7iy3g7/ga7V+oXCR8sDBJA/qj30o2VSJWHF+brwrW3AmPm7Zz86WC6J6maaW+LG0z8HeJgRV97j2PDgTzaPj7S9edxwWl+PhId3PYCMrWP1wjbe+eMA73KZnpk/5gcsluCrhbkkvXvm3HHM+w67/zee/jkA8w2vPLzn/if9puwIMExCou9OnXsNa93H/Ra4Ed9pJW8+vPv+n2GS4dvmoK8u0WW2U9Dm0UNYipxy32082+OAwzOHd/7BYgkdAcaEj2zblk9GBg9sgEZutOfIK/KMvGsvxfpYHXWJ3mzeuXIlKa7dPLHRPXq5st4PRr8p0LK6AYu5dg0YM+GYdnrq48mNiQi5sd5xgrF3931HOhmz1ubWcZfozYiEVGY8G1N+L18qfusYeRMEWMwvkBbmJdJZXJfln7/xEhEpZy3L/mVl7erlUHf+wAEjOafO/me7KBdfBWh7uyOvR1NLeVpY0Z/v2/Xdz4OugRLASOTUlB1bEucgFZHPYIwLpOsNuvJB54duD/tY9h/j4v6ju3e3FuR2Ur4ywFxiTn1wfo8oll9CRZRsgrrlmH/KKRG1fr3vezub7md1S6dywEggW9jpf59/yrnI29arI9Itg1qmlzB2gm39vd/f+XKlhbVM0VUELYC5FJ766JPbZDH3PM44HAaIyq8ddstV8QQ4UAO0j9ux9HP7HthxXUUZ9fLUCphLwDsfXrxTLC8+KyQv5RThtGLiErvmCWlFDnpokyLW98IjD05cXROs/K8RwNxanT373/FCOfc0GHAE2h5j7vswPqHnPoMPbDJhpV7ctes76zRyddFsFDC3kpUZ5cePYbB7AtotB8LSXTrdno0zc1L+NS7u+7uqmZ/Lh3aeoQDMS+g7Fy6Mynz5ELSsDuLOrEcBol4z7TgAjhP7OFNsvWEnrROP3HuvGRVkL1M8/tAB5qGtspaLnN9jl+z9GOD3g9iHMOalvXG69WNMykJwDcMy8qSMyJPx0s4zYWhJjeoVasDWEo2uUp45f35radmeAHDODyfsd4Dhw2A8WiJMutuwEu7a4qe1VUmbxhKa5yKD9zyacglxL/IXicmLe3bu/AJdH7LoDfc/qtmAl6kNqloAAAAASUVORK5CYII=\n";
